import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/** Đặt `undefined` (hoặc chuỗi rỗng) để xoá hẳn tham số khỏi URL. */
export type UrlParamPatch = Record<string, string | undefined>;

interface UpdateOptions {
	/**
	 * `true` để ghi đè mục lịch sử hiện tại thay vì thêm mục mới. Dùng cho thay
	 * đổi KHÔNG phải là điều hướng — đổi bộ lọc chẳng hạn — để nút Back không
	 * bắt người dùng lội ngược qua từng lần chỉnh.
	 */
	replace?: boolean;
}

/**
 * Đọc và ghi query string như một mảnh state, để mỗi tab và mỗi bộ lọc đều có
 * link đi thẳng tới đúng chỗ đó.
 *
 * Nhận NHIỀU tham số trong một lần gọi vì `setSearchParams` không xếp hàng như
 * `setState`: hai lần gọi trong cùng một tick thì lần sau không nhìn thấy thay
 * đổi của lần trước và sẽ nuốt mất nó. Chuyển tab ra khỏi "Khác" phải vừa đặt
 * `tab` vừa xoá `sub`, nên nó buộc phải là một lần ghi duy nhất.
 */
export function useUrlParams(): [
	URLSearchParams,
	(patch: UrlParamPatch, options?: UpdateOptions) => void,
] {
	const [searchParams, setSearchParams] = useSearchParams();

	const update = useCallback(
		(patch: UrlParamPatch, options?: UpdateOptions) => {
			setSearchParams(
				(previous) => {
					const next = new URLSearchParams(previous);
					for (const [key, value] of Object.entries(patch)) {
						if (value === undefined || value === "") next.delete(key);
						else next.set(key, value);
					}
					return next;
				},
				{ replace: options?.replace ?? false },
			);
		},
		[setSearchParams],
	);

	return [searchParams, update];
}
